/**
 * Root-stack routes — the flat, scope-less pages that sit directly on the
 * router root: authentication (login/logout/callback), global staff support
 * search and directories (customers, users, radios), personal user settings,
 * and the entity-reference chip resolver redirects. None of these mount a
 * customer / site-group / site scope layout.
 *
 * Contains:
 * - rootStackRoutes — a fragment of <Route> elements for the composer
 */
import React from 'react';
import { Route } from 'react-router-dom';

import { determineCanSeeSupport, determineIsStaff } from '@sophocles/router/checkCredentials';
import EntityRedirect from '@sophocles/router/EntityRedirect';
import HomeRedirect from '@sophocles/router/HomeRedirect';
import {
  authCallbackPath,
  bluetoothDebugPath,
  customerResolverPath,
  loginPath,
  logoutPath,
  radioResolverPath,
  rootPath,
  searchPath,
  siteGroupResolverPath,
  siteResolverPath,
  supportCustomerCreatePath,
  supportCustomersPath,
  supportRadioDetailPath,
  supportUserCreatePath,
  supportUserManualPasswordPath,
  supportUserPath,
  supportUserSettingsPath,
  supportUserSettingsSectionPath,
  supportUsersPath,
  userHomePath,
  userHomeSectionPath,
  userResolverPath,
  userSettingsPath,
  userSettingsSectionPath,
} from '@sophocles/router/paths';
import RequireAuth from '@sophocles/router/RequireAuth';
import AuthCallback from '@sophocles/views/auth/CallbackView';
import LoginPage from '@sophocles/views/auth/LoginPageView';
import LogoutPage from '@sophocles/views/auth/LogoutPageView';
import CreateCustomerView from '@sophocles/views/customers/CreateCustomerView';
import CustomerGridView from '@sophocles/views/customers/CustomerGridView';
import SearchView from '@sophocles/views/search/SearchView';
// EditUserPage is DEPRECATED(ENG-664) — admin user-edit routes now
// mount UserSettingsViewV2 (imported here as `UserSettingsPage`).
import UserSettingsPage from '@sophocles/views/settings/UserSettingsViewV2';
import BluetoothDebugView from '@sophocles/views/sites/BluetoothDebugView';
import RadioDetailView from '@sophocles/views/support/RadioDetailView';
import CreateUserView from '@sophocles/views/users/CreateUserView';
import EditPasswordView from '@sophocles/views/users/EditPasswordView';
import UserGridView from '@sophocles/views/users/UserGridView';

export const rootStackRoutes = (
  <>
    <Route path={rootPath.pattern} element={<RequireAuth component={HomeRedirect} />} />
    <Route path={loginPath.pattern} element={<LoginPage />} />
    <Route path={logoutPath.pattern} element={<LogoutPage />} />
    <Route path={authCallbackPath.pattern} element={<AuthCallback />} />
    <Route
      path={bluetoothDebugPath.pattern}
      element={<RequireAuth component={BluetoothDebugView} onEnter={determineIsStaff} />}
    />
    <Route path={searchPath.pattern} element={<RequireAuth component={SearchView} />} />
    <Route
      path={supportCustomersPath.pattern}
      element={<RequireAuth component={CustomerGridView} onEnter={determineCanSeeSupport} />}
    />
    <Route
      path={supportCustomerCreatePath.pattern}
      element={<RequireAuth component={CreateCustomerView} onEnter={determineCanSeeSupport} />}
    />
    <Route
      path={supportRadioDetailPath.pattern}
      element={<RequireAuth component={RadioDetailView} onEnter={determineIsStaff} />}
    />

    {/* Support users pages */}
    <Route
      path={supportUsersPath.pattern}
      element={<RequireAuth component={UserGridView} onEnter={determineCanSeeSupport} />}
    />

    <Route
      path={supportUserCreatePath.pattern}
      element={<RequireAuth component={CreateUserView} onEnter={determineCanSeeSupport} />}
    />

    <Route
      path={supportUserPath.pattern}
      element={<RequireAuth component={UserSettingsPage} onEnter={determineCanSeeSupport} />}
    />
    <Route
      path={supportUserSettingsPath.pattern}
      element={<RequireAuth component={UserSettingsPage} onEnter={determineCanSeeSupport} />}
    />
    <Route
      path={supportUserSettingsSectionPath.pattern}
      element={<RequireAuth component={UserSettingsPage} onEnter={determineCanSeeSupport} />}
    />

    <Route
      path={supportUserManualPasswordPath.pattern}
      element={<RequireAuth component={EditPasswordView} onEnter={determineIsStaff} />}
    />

    {/* personal user settings */}
    <Route path={userHomePath.pattern} element={<RequireAuth component={UserSettingsPage} />} />
    <Route path={userHomeSectionPath.pattern} element={<RequireAuth component={UserSettingsPage} />} />
    <Route path={userSettingsPath.pattern} element={<RequireAuth component={UserSettingsPage} />} />
    <Route path={userSettingsSectionPath.pattern} element={<RequireAuth component={UserSettingsPage} />} />

    {/* Entity reference chip resolver routes */}
    <Route
      path={customerResolverPath.pattern}
      element={<RequireAuth component={EntityRedirect} componentProps={{ type: 'customer' }} />}
    />
    <Route
      path={siteGroupResolverPath.pattern}
      element={<RequireAuth component={EntityRedirect} componentProps={{ type: 'site-group' }} />}
    />
    <Route
      path={siteResolverPath.pattern}
      element={<RequireAuth component={EntityRedirect} componentProps={{ type: 'site' }} />}
    />
    <Route
      path={userResolverPath.pattern}
      element={<RequireAuth component={EntityRedirect} componentProps={{ type: 'user' }} />}
    />
    <Route
      path={radioResolverPath.pattern}
      element={<RequireAuth component={EntityRedirect} componentProps={{ type: 'radio' }} />}
    />
  </>
);
